import { useCallback, useState } from 'react';
import { ChatShareRequest } from '../client/SmartAssistantBackendClient';
import { ClientHelper } from '../client/CreateNswagClient';
import { deriveTitle } from '../utils/chatMessageUtils';
import { useCurrentThreadContext } from '../contexts/CurrentThreadContext';
import { useLoginContext } from '../components/login/LoginHandler';

export function useShareChat() {
  const { threadId: chatId, history, agentSelection } = useCurrentThreadContext();
  const consumer = useLoginContext();
  const [shareChatOpen, setShareChatOpen] = useState(false);
  const [shareChatRecipients, setShareChatRecipients] = useState<string[]>([]);
  const [shareChatSubject, setShareChatSubject] = useState('Chatverlauf');
  const [shareChatSending, setShareChatSending] = useState(false);

  const buildChatExportPayload = useCallback(() => {
    const title = deriveTitle(history);
    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      title,
      chatId,
      messages: history,
      agentSelection,
    };
  }, [agentSelection, chatId, history]);

  const buildFileName = useCallback((title?: string | null) => (
    `chat-${(title || 'export').replace(/\s+/g, '-').toLowerCase()}.json`
  ), []);

  const openShareChatDialog = useCallback(() => {
    setShareChatRecipients([]);
    setShareChatSubject(`Chatverlauf: ${deriveTitle(history) || 'Export'}`);
    setShareChatOpen(true);
  }, [history]);

  const addShareChatRecipient = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setShareChatRecipients(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
  }, []);

  const sendChatShare = useCallback(async () => {
    if (shareChatRecipients.length === 0) return;
    const payload = buildChatExportPayload();
    setShareChatSending(true);
    try {
      const token = await consumer.getUserToken();
      const chatClient = ClientHelper.createChatClient(token);
      await chatClient.share(new ChatShareRequest({
        recipients: shareChatRecipients,
        subject: shareChatSubject,
        fileName: buildFileName(payload.title),
        payloadJson: JSON.stringify(payload),
      }));
      setShareChatOpen(false);
    } catch (err) {
      console.error('share failed', err);
    } finally {
      setShareChatSending(false);
    }
  }, [buildChatExportPayload, buildFileName, consumer, shareChatRecipients, shareChatSubject]);

  const downloadChatExport = useCallback(() => {
    const payload = buildChatExportPayload();
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildFileName(payload.title);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      link.remove();
    }, 0);
  }, [buildChatExportPayload, buildFileName]);

  return {
    shareChatOpen,
    setShareChatOpen,
    shareChatRecipients,
    setShareChatRecipients,
    shareChatSubject,
    setShareChatSubject,
    shareChatSending,
    openShareChatDialog,
    addShareChatRecipient,
    sendChatShare,
    downloadChatExport,
  };
}
